import React, { useEffect, useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { TransferOrder, Yard } from '../../../types/stock.types';
import { transfersApi } from '../../../utils/stock-api';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Open yards only (§7.1). */
    yards: Yard[];
    onCreated: (order: TransferOrder) => void;
}

/**
 * New Transfer Order (STOCK.md §7.1).
 *
 * Only the two yards are set here. Everything else — transport vendor, material list,
 * departure date, photos and signature — is added while the transfer is Draft/Packing,
 * because none of it is known when someone first decides to move gear.
 */
export const TransferOrderFormDialog: React.FC<Props> = ({ open, onOpenChange, yards, onCreated }) => {
    const [originYardId, setOriginYardId] = useState('');
    const [destinationYardId, setDestinationYardId] = useState('');
    const [saving, setSaving] = useState(false);

    const yardOptions = Array.isArray(yards) ? yards : [];

    useEffect(() => {
        if (!open) return;
        setOriginYardId('');
        setDestinationYardId('');
    }, [open]);

    // A yard cannot transfer to itself (§7.1), so the chosen origin drops out of the
    // destination list rather than being offered and then rejected.
    const destinationOptions = yardOptions.filter((y) => String(y.id) !== originYardId);

    const canSubmit = Boolean(originYardId && destinationYardId
        && originYardId !== destinationYardId) && !saving;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        try {
            setSaving(true);
            const order = await transfersApi.create({
                originYardId: Number(originYardId),
                destinationYardId: Number(destinationYardId),
            });
            toast.success(`${order.orderNumber} created`);
            onOpenChange(false);
            onCreated(order);
        } catch (err: any) {
            toast.error(err?.data?.message || err?.message || 'Failed to create transfer');
        } finally {
            setSaving(false);
        }
    };

    const branchOf = (id: string) =>
        yardOptions.find((y) => String(y.id) === id)?.branch?.name ?? null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px]">
                <DialogHeader>
                    <DialogTitle>New Transfer Order</DialogTitle>
                    <DialogDescription>
                        Moves material from one yard to another. You'll add the material list,
                        transport and departure evidence before it leaves.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <Label htmlFor="origin-yard">Origin Yard *</Label>
                        <Select value={originYardId} onValueChange={setOriginYardId}>
                            <SelectTrigger id="origin-yard">
                                <SelectValue placeholder={
                                    yardOptions.length ? 'Where the gear is now' : 'No open yards'
                                } />
                            </SelectTrigger>
                            <SelectContent>
                                {yardOptions.map((y) => (
                                    <SelectItem key={y.id} value={String(y.id)}>
                                        {y.yardName} ({y.yardCode})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {/* Branch is derived context, never stored on the transfer (§7). */}
                        {branchOf(originYardId) && (
                            <p className="text-xs text-muted-foreground mt-1">
                                Branch: {branchOf(originYardId)}
                            </p>
                        )}
                    </div>

                    <div className="flex justify-center text-muted-foreground">
                        <ArrowRight size={18} />
                    </div>

                    <div>
                        <Label htmlFor="destination-yard">Destination Yard *</Label>
                        <Select
                            value={destinationYardId}
                            onValueChange={setDestinationYardId}
                            disabled={!originYardId}
                        >
                            <SelectTrigger id="destination-yard">
                                <SelectValue placeholder={
                                    !originYardId
                                        ? 'Pick an origin first'
                                        : destinationOptions.length
                                            ? 'Where it is going'
                                            : 'No other open yards'
                                } />
                            </SelectTrigger>
                            <SelectContent>
                                {destinationOptions.map((y) => (
                                    <SelectItem key={y.id} value={String(y.id)}>
                                        {y.yardName} ({y.yardCode})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {branchOf(destinationYardId) && (
                            <p className="text-xs text-muted-foreground mt-1">
                                Branch: {branchOf(destinationYardId)}
                            </p>
                        )}
                        {originYardId && destinationOptions.length === 0 && (
                            <p className="text-xs text-muted-foreground mt-1">
                                There is only one open yard. Create another before transferring.
                            </p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!canSubmit}>
                            {saving && <Loader2 size={16} className="animate-spin mr-2" />}
                            Create Transfer
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default TransferOrderFormDialog;
